import { Autocomplete, Box, CircularProgress, TextField } from '@mui/material';
import React, { useEffect, useState } from 'react';
import {
  useGetApiProgramsQuery,
  useLazyGetApiProgramsByIdWorkflowTypesQuery,
} from '../../app/athenaApiGen';

type TWorkflowTypesFilterProps = {
  handleChange: (type?: string | null) => void;
  required?: boolean;
  workflowType?: string;
};

const WorkflowTypesFilter = (props: TWorkflowTypesFilterProps) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<string[]>([]);
  // The deliverable modal has no program selector, so scope the workflow types
  // to the caller's program: GET /api/programs is ACL-filtered to the user's
  // programs, and the endpoint below filters to Deliverable-bound types enabled
  // for that program (excludes TAR/Action-Item types and, for IMCS, Standard
  // Approval). No project is required and the field is never disabled.
  const { data: programs } = useGetApiProgramsQuery();
  const programId = programs?.[0]?.programId;
  const [queryTypes, { isFetching: isLoading }] =
    useLazyGetApiProgramsByIdWorkflowTypesQuery();

  const handleOpen = async () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  useEffect(() => {
    if (!programId) {
      return;
    }
    (async () => {
      try {
        const types = await queryTypes({
          id: programId,
          objectType: 'Deliverable',
        }).unwrap();
        // Order from least- to most-involved workflow, independent of the
        // order the API returns. Unknown/other types sink to the bottom.
        const order = ['No Workflow', 'Internal Approval Only'];
        const rank = (t: string) => {
          const i = order.indexOf(t);
          return i === -1 ? order.length : i;
        };
        setOptions([...(types ?? [])].sort((a, b) => rank(a) - rank(b)));
      } catch (err) {
        console.log(err);
      }
    })();
  }, [queryTypes, programId]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
      <Autocomplete
        open={open}
        onOpen={handleOpen}
        onClose={handleClose}
        isOptionEqualToValue={(option, value) => option === value}
        getOptionLabel={(option) => option ?? ''}
        options={options}
        loading={isLoading}
        onChange={(_, newValue) => {
          props.handleChange(newValue ?? null);
        }}
        value={options.find((opt) => opt === props?.workflowType) || null}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Workflow Type"
            required={props.required}
            size="small"
            slotProps={{
              input: {
                ...params.InputProps,
                endAdornment: (
                  <React.Fragment>
                    {isLoading ? (
                      <CircularProgress color="inherit" size={20} />
                    ) : null}
                    {params.InputProps.endAdornment}
                  </React.Fragment>
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default WorkflowTypesFilter;
